import classNames from 'classnames';
import styles from './VehicleCandidateHistory.module.css';
import { Table, ConfigProvider, Spin, Button, Modal, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState, useEffect } from 'react';
import axios from 'axios';

export interface DataType {
  eventDateTime: string;
  sessionDateTime: string;
  sourceType: string;
  candidateId: string;
  candidateName: string;
  timezoneOffsetMinutes: string;
  sourceFile: string;
  startDatetime: string;
  endDatetime: string;
  topixCode: string;
  topixDealerName: string;
  topixPostalAddress: string;
  topixState: string;
  topixPostalCode: string;
  topixCountry: string;
  topixUserType: string;
  topixUserLocale: string;
  deviceInfo: string;
  applicationDuration: string;
  applicationCompletionStatus: string;
  odoKm: string;
  odoMiles: string;
  sessionId: string;
}
interface Data {
  [key: string]: string;
}
interface CandidateHistoryProps {
  propsData: Data[];
}
type FileType = '.zip' | '.csv';
type QueryType = 'session' | 'someOtherType';

interface QueryParameter {
  sessionId: string;
  sessionTimestamp: string;
}

const VehicleCandidateHistory: React.FC<CandidateHistoryProps> = ({ propsData }) => {
  const [data, setData] = useState<Data[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [isDownloadPending, setIsDownloadPending] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSession, setSelectedSession] = useState<Data | null>(null);
  const handleRowClick = (record: Data) => {
    setSelectedSession(record);
    setModalVisible(true);
  };
  useEffect(() => {
    setIsLoading(true);
    if (propsData && propsData.length > 0) {
      propsData.forEach((n: any) => {
        n.sessionTimestampString = new Date(n.sessionTimestamp).toLocaleString();
        n.eventTimestampString = new Date(n.eventTimestamp).toLocaleString();
      });
      const sortedData = propsData.sort((a: Data, b: Data) => {
        return new Date(b.eventTimestamp).getTime() - new Date(a.eventTimestamp).getTime();
      });

      setData([...sortedData]);
    }
  }, [propsData]);
  useEffect(() => {
    setIsLoading(false);
  }, [data]);
  useEffect(() => {
    let lastCopied = '';
    let messageVisible = false;
    let hideTimeout: NodeJS.Timeout;

    const handleMouseUp = () => {
      const selectedText = window.getSelection()?.toString().trim();

      if (selectedText && selectedText !== lastCopied) {
        lastCopied = selectedText;

        navigator.clipboard
          .writeText(selectedText)
          .then(() => {
            if (!messageVisible) {
              messageVisible = true;
              message.destroy(); // remove any lingering messages
              message.success('Copied to clipboard', 1.2);

              hideTimeout = setTimeout(() => {
                messageVisible = false;
              }, 1200); // match toast duration
            }
          })
          .catch((err) => {
            console.error('Clipboard copy failed:', err);
            message.destroy();
            message.error('Failed to copy');
          });
      }
    };

    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      clearTimeout(hideTimeout);
    };
  }, []);

  const getSddSessionInfo = async (
    queryParameter: { sessionId: string; sessionTimestamp: string },
    // userId: string,
    token: string
  ): Promise<string> => {
    const downloadUrl = `${process.env.REACT_APP_API_URL}/viewer/session/info`;
    // if (userId) {
    //   downloadUrl += `?userId=${encodeURIComponent(userId)}`;
    // }

    const response = await axios.post(downloadUrl, queryParameter, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      responseType: 'text',
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch session info');
    }
  };

  const getDownloadUrl = (
    fileType: FileType,
    queryType: QueryType,
    // userId: string,
    offset?: number
  ): string => {
    let baseUrl = `${process.env.REACT_APP_API_URL}/viewer/${queryType}`;

    if (queryType === 'session') {
      baseUrl += fileType === '.zip' ? '/summary/export' : '/export';
    } else {
      baseUrl = `${process.env.REACT_APP_API_URL}/${queryType}/query/final/export`;
    }

    // Construct query parameters
    const params = new URLSearchParams();

    // if (userId) {
    //   params.append('userId', userId);
    // }

    if (offset !== undefined) {
      params.append('offset', offset.toString());
    }

    // Append query parameters if they exist
    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
  };

  const handleDownloadZip = async () => {
    if (!selectedSession) return;

    // const userId: string = localStorage.getItem('userId') || '';
    const token: string | null = localStorage.getItem('id_token');

    if (!token) {
      console.error('No auth token found!');
      return;
    }

    if (isDownloadPending) return;
    setIsDownloadPending(true);

    try {
      const downloadUrl: string = getDownloadUrl('.zip', 'session');

      const payload = {
        sessionId: selectedSession.sessionId,
        sessionTimestamp: selectedSession.sessionTimestamp,
      };

      const response = await axios.post(downloadUrl, payload, {
        responseType: 'blob', // Ensures the response is treated as a file
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      let fileName = 'download.zip';
      const contentDisposition = response.headers['content-disposition'];

      if (contentDisposition) {
        const filenameRegex = /filename=["']?([^"';]+)["']?/;
        const matches = contentDisposition.match(filenameRegex);

        if (matches && matches[1]) {
          fileName = matches[1]; // Correctly extracts "example.zip"
        }
      }

      // Handle file download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading the file:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsDownloadPending(false);
    }
  };
  const handleViewSession = async () => {
    if (!selectedSession) return;

    setIsLoading(true);

    // const userId: string = decodeURIComponent(localStorage.getItem('userId')) || '';
    const token: string | null = localStorage.getItem('id_token');

    if (!token) {
      console.error('No auth token found!');
      setIsLoading(false);
      return;
    }

    const queryParameter = {
      sessionId: selectedSession.sessionId,
      sessionTimestamp: selectedSession.sessionTimestamp,
    };

    try {
      const sddSummaryHTML = await getSddSessionInfo(queryParameter, token);

      // Open a new window and display the session info
      const windowRef = window.open('/', '_blank', 'scrollbars,resizable,height=800,width=1325');
      if (windowRef) {
        const sessionViewerPlaceholder = `<div style='overflow-y:auto;overflow-x:hidden;height:100%;width:100%'>${sddSummaryHTML}</div>`;
        windowRef.document.write(sessionViewerPlaceholder);
      }
    } catch (err) {
      console.error('Error fetching session info:', err);
      setError(error instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const columns: ColumnsType<Data> = [
    {
      title: 'Event Date/Time',
      dataIndex: 'eventTimestampString',
      key: 'eventTimestampString',
      fixed: 'left',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Session Date/Time',
      dataIndex: 'sessionTimestampString',
      key: 'sessionTimestampString',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Source Type',
      dataIndex: 'sourceType',
      key: 'sourceType',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Candidate Id',
      dataIndex: 'applicationDescription',
      key: 'applicationDescription',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Candidate Name',
      dataIndex: 'candidateName',
      key: 'candidateName',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Time zone Offset Minutes',
      dataIndex: 'timezoneOffsetMinutes',
      key: 'timezoneOffsetMinutes',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Source File',
      dataIndex: 'sourceFile',
      key: 'sourceFile',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Start Date/Time',
      dataIndex: 'startDatetime',
      key: 'startDatetime',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'End Date/Time',
      dataIndex: 'endDatetime',
      key: 'endDatetime',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Topix Code',
      dataIndex: 'topixCode',
      key: 'topixCode',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Topix Dealer Nmae',
      dataIndex: 'topixDealerName',
      key: 'topixDealerName',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Topix Postal Address',
      dataIndex: 'topixPostalAddress',
      key: 'topixPostalAddress',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Topix State',
      dataIndex: 'topixState',
      key: 'topixState',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Topix Postal Code',
      dataIndex: 'topixPostalCode',
      key: 'topixPostalCode',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Topix Country',
      dataIndex: 'topixCountry',
      key: 'topixCountry',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Topix User Type',
      dataIndex: 'topixUserType',
      key: 'topixUserType',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Topix User Locale',
      dataIndex: 'topixUserLocale',
      key: 'topixUserLocale',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Device Info',
      dataIndex: 'deviceInfo',
      key: 'deviceInfo',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Application Duration',
      dataIndex: 'applicationDuration',
      key: 'applicationDuration',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Application Completion Status',
      dataIndex: 'applicationCompletionStatus',
      key: 'applicationCompletionStatus',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Odo (Km)',
      dataIndex: 'odoKm',
      key: 'odoKm',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Odo (Miles)',
      dataIndex: 'odoMiles',
      key: 'odoMiles',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Session ID',
      dataIndex: 'sessionId',
      key: 'sessionId',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
  ];

  const tableTheme = {
    components: {
      Table: {
        headerBg: '#dcdcdc',
        headerSplitColor: 'white',
        headerBorderRadius: 0,
        cellFontSize: 14,
      },
    },
  };

  // TODO: Implement logic to populate data in the table with api call
  return (
    <div className={classNames(styles.VehicleCandidateHistory)}>
      <ConfigProvider theme={tableTheme}>
        <Table
          columns={columns}
          dataSource={data}
          scroll={{ x: 'max-content', y: 300 }}
          pagination={false}
          rowKey="id"
          loading={{
            spinning: isLoading,
            indicator: <Spin className={classNames(styles['loader-color'])} />,
          }}
          onRow={(record) => ({
            onClick: (e) => {
              const selection = window.getSelection();
              const isTextSelected = selection && selection.toString().length > 0;

              if (!isTextSelected) {
                handleRowClick(record);
              }
            },
          })}
        />
      </ConfigProvider>
      <Modal
        title="Session"
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedSession && (
          <div>
            <p>{selectedSession.sessionId}</p>
            {selectedSession.sourceType == 'Topix' ? (
              <div className={classNames(styles['VehicleCandidateHistory--topix-notes-container'])}>
                <p>
                  TOPIX data cannot currently be downloaded in VFDB, please use{' '}
                  <a href="https://topix.jaguar.jlrext.com" target="_blank" rel="noreferrer">
                    topix.jaguar.jlrext.com
                  </a>
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                <Button
                  onClick={handleViewSession}
                  className={classNames(styles['VehicleCandidateHistory--Buttons'])}
                >
                  View Session
                </Button>
                <Button
                  onClick={handleDownloadZip}
                  className={classNames(styles['VehicleCandidateHistory--Buttons'])}
                  style={{ marginRight: 20 }}
                >
                  Download ZIP
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VehicleCandidateHistory;
